// src/ui/settings/haptics.ts — render-tier haptics subscriber (D-03).
//
// A one-way renderBus subscriber (the audioManager pattern, manager.ts
// lines 225-308): subscribe to sim events, fire a browser side effect, and
// NEVER emit back to the bus (AUDIO-01 invariant). Pure render/UI — no sim
// import.
//
// Both the Vibration settings ROW and the vibrate CALL are capability-gated on
// `'vibrate' in navigator`: iOS Safari has no Vibration API, so the row is
// omitted (never a broken/disabled control) and the call is a no-op there.

import { renderBus } from '../../render/bus';

import { getSettings } from './store';

// 07-PATTERNS.md vibration patterns: catch ≈ a single firm buzz; foxguard ≈ a
// three-pulse "called" rhythm.
const CATCH_PATTERN: number[] = [40];
const FOXGUARD_PATTERN: number[] = [20, 40, 20];

/** True when the browser exposes the Vibration API (drives row visibility). */
export function supportsVibration(): boolean {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
}

/** Fire navigator.vibrate ONLY when enabled in settings AND supported. */
function buzz(pattern: number[]): void {
  if (getSettings().vibration && supportsVibration()) {
    navigator.vibrate(pattern);
  }
}

/**
 * Subscribe to the catch / foxguard-called sim events and buzz on each.
 * Returns a teardown fn that unsubscribes all handlers (manager.ts cleanup
 * loop idiom).
 */
export function initHaptics(): () => void {
  const offFns: Array<() => void> = [];

  offFns.push(
    renderBus.on('catch', () => {
      buzz(CATCH_PATTERN);
    }),
  );
  offFns.push(
    renderBus.on('foxguard-called', () => {
      buzz(FOXGUARD_PATTERN);
    }),
  );

  return (): void => {
    for (const off of offFns) off();
    offFns.length = 0;
  };
}
